import { http } from "../utils/http";
import { API_ENDPOINTS, PAGE_SIZE, PATIENT_API_ENDPOINTS } from "../config";
import { ROLE_PATIENT } from "../security/roles";
import { store } from "../State/store/configureStore";
import StorageService from "./storageService";
import { closeProgress, showProgress } from "../State/actions/uploadProgress";
import moment from "moment";
import recentPatientsStorageService from "./recentPatientsStorageService";
import globalCacheService from "./globalCacheService";
import { devLog } from "../utils/devLog";
export const RECENT_PATIENTS = "RECENT_PATIENTS";

// Helper function to create consistent cache keys for patient queries
function createCacheKey(type, page, criteria) {
  // Create a deterministic key based on the query parameters
  const key = {
    mode: criteria?.mode || "default",
    searchTerm: criteria?.searchTerm || "",
    sortField: criteria?.sortField || "",
    sortDirection: criteria?.sortDirection || "",
    uiFields: criteria?.uiFields || [],
    patientTypeId: criteria?.patientTypeId || "",
    statField: criteria?.statField || "",
    stat: criteria?.stat || "",
    showMessageChannels: criteria?.showMessageChannels || false,
    page: page || 0,
  };

  // Convert to a consistent string representation
  const keyString = JSON.stringify(key);

  // Encode Unicode string to base64 safely
  // btoa() only handles Latin1, so we use encodeURIComponent + unescape to convert Unicode to Latin1
  const base64 = btoa(unescape(encodeURIComponent(keyString)));

  return `patients_${type}_${base64.replace(/[+/=]/g, "")}`; // Remove special chars from base64
}

function preProcessFilterFields(criteria) {
  let uiFields = criteria ? criteria.uiFields : {};
  if (uiFields && uiFields.length > 0) {
    uiFields.forEach((attribute) => {
      attribute.operator = attribute.operator || "EQUALS";

      // Remove unnecessary options array from payload - it's just UI metadata
      if (attribute.options) {
        console.log(`Removing ${attribute.options.length} options from ${attribute.name} filter to reduce payload size`);
        delete attribute.options;
      }

      // Remove other UI-only properties
      delete attribute.choiceList;
      delete attribute.operatorsSupported;
    });
  }
  return uiFields;
}

function createFormData(criteria, uiFieldsArr = []) {
  let formData = {
    mode: criteria ? criteria.mode : null,
  };

  if (uiFieldsArr.length > 0) {
    formData.uiFields = uiFieldsArr;
  }

  if (criteria.searchTerm) {
    formData.searchTerm = criteria.searchTerm.trim();
    //KUMAR March 8 2025 MYP-6810 trim leading and trailing spaces
  }

  if (criteria && criteria.sortField) {
    formData.sortField = criteria.sortField;
    formData.sortDirection = criteria.sortDirection;
  }

  if (criteria.recentPatientIds) {
    formData.recentPatientIds = criteria.recentPatientIds;
  }

  if (criteria.statField) {
    formData.statField = criteria.statField;
    formData.stat = criteria.stat;
  }

  if (criteria.showMessageChannels) {
    formData.showChannelsOnly = criteria.showMessageChannels;
  }

  if (criteria.additionalObservations) {
    formData.additionalObservations = criteria.additionalObservations;
  }

  if (criteria.patientTypeId) {
    formData.patientTypeId = criteria.patientTypeId;
  }
  if(criteria.sortDirection){
    formData.sortDirection = criteria.sortDirection;
  }

  return formData;
}

class PatientsService {
  // Helper method to invalidate patient-related caches
  invalidatePatientCaches() {
    globalCacheService.clearCachesByPrefix("patients_");
  }

  // UIFields caching method
  async getUIFields() {
    const cacheKey = "uiFields_attributeUIFields";

    try {
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          console.log("[patientsService] Loading uiFields from API");
          // Get uiFields from user data via userService - this matches the login flow
          const UserService = require("./userService").default;
          const userInfo = await UserService.getSpaceInfo();
          return userInfo.attributeUIFields || [];
        },
        10 * 60 * 1000 // 10 minutes cache for uiFields
      );
    } catch (error) {
      console.error("[patientsService] Error loading uiFields:", error);
      return [];
    }
  }

  // Helper method to update patient tag status across all relevant caches
  updatePatientTagStatusInCaches(patientId, newTaggedToMeStatus) {
    //devLog(
    //  "[patientsService] Updating patient tag status in all caches:",
    //  patientId,
    //  newTaggedToMeStatus
    //);

    const allKeys = Object.keys(window.MYP_API_CACHE || {});
    let updatedKeys = [];
    let removedFromWatchlistKeys = [];

    allKeys.forEach((key) => {
      if (key.startsWith("patients_list_")) {
        try {
          const parts = key.split("_");
          if (parts.length >= 3) {
            const base64Part = parts.slice(2).join("_");
            const decoded = atob(base64Part);
            //devLog("[patientsService] Checking cache key:", decoded);

            // Update both ALL and TAGGED (watchlist) patient list caches
            if (
              decoded.includes("GETPATIENTS_ALL") ||
              decoded.includes("GETPATIENTS_TAGGED")
            ) {
              const cacheEntry = window.MYP_API_CACHE[key];
              if (cacheEntry && cacheEntry.data && cacheEntry.data.data) {
                const patientList = cacheEntry.data.data;
                const patientIndex = patientList.findIndex(
                  (p) => p.patientId === patientId
                );

                if (patientIndex !== -1) {
                  //devLog("[patientsService] Found patient in cache key:", key);

                  if (
                    decoded.includes("GETPATIENTS_TAGGED") &&
                    !newTaggedToMeStatus
                  ) {
                    // For watchlist cache: if removing from watchlist, remove patient entirely
                    //devLog(
                    //  "[patientsService] Removing patient from watchlist cache:",
                    //  key
                    //);
                    patientList.splice(patientIndex, 1);
                    removedFromWatchlistKeys.push(key);
                  } else {
                    // For ALL cache or when adding to watchlist: just update the tag status
                    //devLog(
                    //  "[patientsService] Updating patient tag status in cache:",
                    //  key
                    //);
                    patientList[patientIndex] = {
                      ...patientList[patientIndex],
                      taggedtoMe: newTaggedToMeStatus,
                    };
                    updatedKeys.push(key);
                  }
                }
              }
            }
          }
        } catch (e) {
          //devLog("[patientsService] Could not process cache key:", key, e);
        }
      }
    });

    //devLog(
    //  "[patientsService] Updated patient tag status in cache keys:",
    //  updatedKeys
    //);
    //devLog(
    //  "[patientsService] Removed patient from watchlist cache keys:",
    //  removedFromWatchlistKeys
    //);
  }
  inviteAdminUser(patientId, data) {
    return http.request(
      API_ENDPOINTS.inviteAdminUser(
        patientId,
        data.hasOwnProperty("loginUserId")
      ),
      "post",
      data
    );
  }

  inviteSpaceMembers(patientId, data) {
    return http.request(
      API_ENDPOINTS.inviteSpaceMember(
        patientId,
        data.hasOwnProperty("loginUserId")
      ),
      "post",
      data
    );
  }

  getSpaceDetails(inviteCode, email) {
    return http.request(API_ENDPOINTS.getSpaceDetails(), "post", {
      inviteCode,
      email,
    });
  }

  add(data, internal, addAsChannel, channelName) {
    let api = null;
    if (internal) {
      api =
        API_ENDPOINTS.addPatientWithoutInvite() + "?makeActiveInternal=true";
    } else if (addAsChannel) {
      api = API_ENDPOINTS.addPatientAsChannel(channelName);
    } else {
      api = API_ENDPOINTS.addPatient(data.hasOwnProperty("loginUserId"));
    }

    return http.request(api, "post", data).then((result) => {
      // Invalidate patient caches when adding a new patient
      this.invalidatePatientCaches();
      return result;
    });
  }

  addChannel(data) {
    let api = API_ENDPOINTS.addPatientAsChannel();
    return http.request(api, "post", data).then((result) => {
      this.invalidatePatientCaches();
      return result;
    });
  }

  deleteChannel(channelId) {
    let api = API_ENDPOINTS.deletePatient(channelId);
    return http.request(api, "delete").then((result) => {
      this.invalidatePatientCaches();
      return result;
    });
  }

  delete(patientId, status, deleteContacts) {
    let api = API_ENDPOINTS.deletePatient(patientId) + "?";
    if (status) {
      api = api + `requestedStatus=${status}`;
    }
    if (deleteContacts) {
      api += "&deleteContacts=true";
    }
    return http.request(api, "delete").then((result) => {
      // Invalidate patient caches when deleting a patient
      this.invalidatePatientCaches();
      return result;
    });
  }

  async update(data) {
    const result = await http.request(
      API_ENDPOINTS.update_patient + data.patientId,
      "patch",
      data
    );
    // Invalidate patient caches when updating a patient
    this.invalidatePatientCaches();
    return result;
  }

  async loadSingle(patientId) {
    const endpoint = API_ENDPOINTS.loadPatient(patientId);
    const baseURL = http.axios.defaults.baseURL;
    const fullURL = baseURL + endpoint;

    /*console.log("[PATIENTS SERVICE DEBUG] loadSingle called:", {
      patientId,
      endpoint,
      baseURL,
      fullURL,
      hasAuthHeader: !!http.axios.defaults.headers.common.Authorization,
    });*/

    try {
      const result = await http.request(fullURL);
      return result;
    } catch (error) {
      console.error("[PATIENTS SERVICE DEBUG] loadSingle error:", error);
      throw error;
    }
  }

  async loadMembers(patientId, page) {
    let role = store.getState().user.role;
    let api = null;
    if (role === ROLE_PATIENT) {
      api = API_ENDPOINTS.patientMembers + "?page=0&size=1000";
    } else {
      api = API_ENDPOINTS.loadMembers(patientId) + "?page=0&size=1000";
    }

    const data = await http.request(api);
    if (data && data._embedded) {
      return {
        data: data._embedded.memberResources || [],
        loadMore: false,
      };
    } else {
      return {
        data: [],
        loadMore: false,
      };
    }
  }

  async loadPatientsWithBasicDetails(searchTerm, pageNumber) {
    let api =
      API_ENDPOINTS.patientsWithBasicDetails + "?size=20&page=" + pageNumber;
    if (searchTerm) {
      api += "&q=" + searchTerm;
    }

    const response = await http.request(api);
    if (response && response._embedded) {
      return {
        patients: response._embedded.patients || [],
        pageInfo: response.page,
      };
    }
    return {
      patients: [],
      pageInfo: { number: -1 },
    };
  }

  /**
   * Load patient count based on criteria
   * @param {Object} criteria - Filter criteria
   * @returns {Promise<Object>} Count response
   */
  async loadCount(criteria) {
    try {
      // Create cache key for this count query
      const cacheKey = createCacheKey("count", 0, criteria);

      // Use cache with 2 minute expiration for counts
      return await globalCacheService.cacheApiCall(
        cacheKey,
        async () => {
          const uiFieldsArr = preProcessFilterFields(criteria);
          const formData = createFormData(criteria, uiFieldsArr);
          return await http.request(
            API_ENDPOINTS.loadPatientsCount(),
            "post",
            formData
          );
        },
        2 * 60 * 1000 // 2 minutes cache for counts
      );
    } catch (error) {
      console.error("Error loading patient count:", error);
      throw error;
    }
  }

  /**
   * Load patients based on criteria, with pagination
   * @param {number} page - Page number for pagination (0-based)
   * @param {Object} criteria - Filter criteria
   * @param {boolean} all - Whether to load all patients
   * @param {number} count - Total count of patients (if known)
   * @param {number} pageSizeOverride - Optional override for page size (useful for exports)
   * @returns {Promise<Object>} Patients data with pagination info
   */
  async load(page, criteria = {}, all, count, pageSizeOverride) {
    try {
      // Determine appropriate page size
      let pageSize = all ? 10 : 10;

      // If pageSizeOverride is provided, use it (for exports)
      if (pageSizeOverride) {
        pageSize = pageSizeOverride;
      } else if (criteria.recentPatientIds) {
        pageSize = 100; // Use larger page size for recent patients
      } else if (count < 101) {
        pageSize = 100; // Load them all if fewer than 101
      }

      // Create cache key for this query
      const cacheKey = createCacheKey("list", page, { ...criteria, pageSize });

      //devLog("[patientsService] Generated cache key:", cacheKey);
      //devLog("[patientsService] Cache key criteria:", {
      //  ...criteria,
      //  pageSize,
      //});

      const hasCache = globalCacheService.hasData(cacheKey);
      //devLog("[patientsService] Cache exists:", hasCache);

      //if (hasCache) {
      //  devLog("[patientsService] Using CACHED data - no API call needed");
      //}

      // Extract API call logic for reuse
      const makeApiCall = async () => {
        const logPrefix = criteria.recentPatientIds
          ? "[patientsService] RECENT PATIENTS (no cache) - making HTTP request"
          : "[patientsService] Cache MISS - making actual HTTP request";


        //devLog(logPrefix);

        // Build API endpoint
        const endPoint =
          API_ENDPOINTS.loadPatients() + `?page=${page}&size=${pageSize}`;

        // Process filter criteria
        const uiFieldsArr = preProcessFilterFields(criteria);
        const formData = createFormData(criteria, uiFieldsArr);

        //devLog("[patientsService] Making API request:", {
        //  endPoint,
        //  formData,
        //  uiFieldsArr,
        //  criteriaPatientTypeId: criteria.patientTypeId,
        //  isRecentPatients: !!criteria.recentPatientIds,
        //});

        // Make API request
        //devLog("[patientsService] About to call http.request with:", {
        //  endPoint,
        //  method: "post",
        //  formData,
        //});

        const data = await http.request(endPoint, "post", formData);

        //devLog("[patientsService] http.request returned:", !!data);
        //devLog(
        //  "[patientsService] Response data sample:",
        //  data ? Object.keys(data) : "no data"
        //);

        // Process response
        if (data && data._embedded) {
          return {
            data: data._embedded.getAllPatientResources || [],
            loadMore: data.page.totalPages > page + 1,
            total: data.page.totalElements,
          };
        } else {
          return {
            data: [],
            loadMore: false,
            total: 0,
          };
        }
      };

      // Skip cache for recent patients OR when using pageSizeOverride (e.g., exports)
      if (criteria.recentPatientIds || pageSizeOverride) {
        const reason = criteria.recentPatientIds
          ? "recent patients"
          : `pageSizeOverride=${pageSizeOverride} (export)`;
        //devLog(
        //  `[patientsService] BYPASSING cache for ${reason} - fetching fresh data`
        //);
        return await makeApiCall();
      } else {
        // Use cache with 3 minute expiration for regular patient lists
        return await globalCacheService.cacheApiCall(
          cacheKey,
          makeApiCall,
          3 * 60 * 1000 // 3 minutes cache for lists
        );
      }
    } catch (error) {
      console.error("Error loading patients:", error);
      return {
        data: [],
        loadMore: false,
        total: 0,
      };
    }
  }

  /**
   * Send messages in bulk to patients matching criteria
   * @param {Object} criteria - Filter criteria to select patients
   * @param {Object} messageData - Message content data
   * @returns {Promise<Object>} API response
   */
  sendMessagesInBulk = async (criteria, messageData) => {
    try {
      const endPoint = API_ENDPOINTS.loadPatients();
      const uiFieldsArr = preProcessFilterFields(criteria);
      const formData = {
        mode: criteria ? criteria.mode : null,
        actionMode: true,
      };

      // Add UI fields if present
      if (uiFieldsArr.length > 0) {
        formData.uiFields = uiFieldsArr;
      }

      // Add search term if present
      if (criteria.searchTerm) {
        formData.searchTerm = criteria.searchTerm;
      }

      // Add message data
      formData.message = messageData.reminderMessage;
      formData.emailMessage = messageData.reminderEmailMessage;
      formData.messageEmailSubject = messageData.reminderMessageEmailSubject;
      formData.statusMessage = messageData.reminderStatusMessage;

      // Make API request
      return await http.request(endPoint, "post", formData);
    } catch (error) {
      console.error("Error sending bulk messages:", error);
      throw error;
    }
  };

  async loadMyPatients(page, criteria) {
    let endPoint =
      API_ENDPOINTS.loadMyPatients + `?page=${page}&size=${PAGE_SIZE}`;
    if (criteria) {
      if (criteria.name) {
        endPoint += "&q=" + criteria.name;
      }
    }

    const data = await http.request(endPoint);
    if (data && data._embedded) {
      return {
        data: data._embedded.patients || [],
        loadMore: data.page.totalPages > page + 1,
        total: data.page.totalElements,
      };
    } else {
      return {
        data: [],
        loadMore: false,
      };
    }
  }

  loadArtifact(artifactLink) {
    return http.request(artifactLink);
  }

  async loadObservationHistory(_, observationCode, page) {
    const pagination = `&page=${page}&size=30`;
    const response = await http.request(
      API_ENDPOINTS.patientGetObservationHistory(observationCode) + pagination
    );
    return {
      data: response._embedded?.observationObjects || [],
      page: response.page,
    };
  }

  loadPatientProfile(patientId) {
    return http.request(API_ENDPOINTS.getPatientProfile(patientId));
  }

  addNewDocument(patientId, document, sendAsStatusMessage = false) {
    return http.request(API_ENDPOINTS.addDocument(patientId, sendAsStatusMessage), "post", document);
  }

  async loadPatientDocuments(
    patientId,
    pageNumber,
    pageSize = 20,
    selectedTopic
  ) {
    let role = store.getState().user.role;
    let api = null;
    if (role === ROLE_PATIENT) {
      api = API_ENDPOINTS.patientDocuments + "&page=0&size=500";
    } else {
      api =
        API_ENDPOINTS.loadPatientDocuments(patientId) +
        `&page=${pageNumber}&size=${pageSize}`;

      if (selectedTopic) {
        api += "&artifactHint=" + selectedTopic;
      }
    }

    const data = await http.request(api);
    return {
      documents:
        data && data._embedded && data._embedded.patientDocuments
          ? data._embedded.patientDocuments
          : [],
      pageInfo: data.page ? data.page : { number: 0, totalPages: 0 },
    };
  }

  async loadPatientDevices(patientId) {
    let role = store.getState().user.role;
    let api = null;
    if (role === ROLE_PATIENT) {
      api = API_ENDPOINTS.patientDevices;
    } else {
      api = API_ENDPOINTS.loadPatientDevices(patientId);
    }

    const data = await http.request(api);
    return data && data._embedded ? data._embedded.provisionedDevices : [];
  }

  updateProfileAttribute(patientId, formData) {
    return http.request(
      API_ENDPOINTS.updatePatientProfileAttribute(patientId),
      "post",
      formData
    );
  }

  updateProfileAttributesMulti(patientId, formData) {
    return http.request(
      API_ENDPOINTS.updatePatientProfileAttributesMulti(patientId),
      "post",
      formData
    );
  }

  addMember(patientId, data, admin) {
    let role = store.getState().user.role;
    let api = null;
    if (role === ROLE_PATIENT) {
      api = API_ENDPOINTS.patientMembers;
    } else {
      api = API_ENDPOINTS.addMember(
        data.hasOwnProperty("loginUserId"),
        patientId
      );
    }

    return http.request(api, "post", data);
  }

  removeMember(patientId, memberId) {
    let role = store.getState().user.role;
    let api = null;
    if (role === ROLE_PATIENT) {
      api = API_ENDPOINTS.patientMembers + `/${memberId}`;
    } else {
      api = API_ENDPOINTS.removeMember(patientId, memberId);
    }
    return http.request(api, "delete");
  }

  editMember(patientId, memberId, data) {
    return http.request(
      API_ENDPOINTS.editMember(patientId, memberId),
      "patch",
      data
    );
  }

  reinvitePatient(patientId, data) {
    return http.request(API_ENDPOINTS.reInvitePatient(patientId), "post", data);
  }

  tagPatient(patientId) {
    return http.request(API_ENDPOINTS.tagPatient(patientId), "post");
  }

  untagPatient(patientId) {
    return http.request(API_ENDPOINTS.tagPatient(patientId), "delete");
  }

  restorePatient(patientId) {
    return http.request(API_ENDPOINTS.restorePatient(patientId), "post");
  }

  addToRecent(patientId) {
    // Use new IndexedDB-based service for staff-specific recent patients
    recentPatientsStorageService.addToRecent(patientId);
  }

  resetCredentials(patientId, data) {
    return http.request(
      API_ENDPOINTS.resetPatientCredentials(patientId),
      "post",
      data
    );
  }

  activatePatient(patientId, data) {
    return http.request(API_ENDPOINTS.activatePatient(patientId), "post", data);
  }

  deleteDocument(patientId, docId) {
    return http.request(API_ENDPOINTS.documentById(patientId, docId), "delete");
  }

  editDocument(patientId, docId, data) {
    return http.request(
      API_ENDPOINTS.editPatientDocument(patientId, docId),
      "patch",
      data
    );
  }

  loadPatientsForMPSAdmin(searchKey, page = 0) {
    return http
      .request(
        API_ENDPOINTS.MPSAdminPatients() + "?size=20&page=" + page,
        "post",
        { searchKey }
      )
      .then((data) => {
        if (data) {
          return {
            patients: data._embedded ? data._embedded.patients || [] : [],
            pageInfo: data.page,
          };
        } else {
          return {
            patients: [],
            pageInfo: { number: -1 },
          };
        }
      });
  }

  loadPatientsForMPSSupport(searchKey, page = 0) {
    return http
      .request(
        API_ENDPOINTS.MPSSupportPatients() + "?size=20&page=" + page,
        "post",
        { searchKey }
      )
      .then((data) => {
        if (data) {
          return {
            patients: data._embedded ? data._embedded.patients || [] : [],
            pageInfo: data.page,
          };
        } else {
          return {
            patients: [],
            pageInfo: { number: -1 },
          };
        }
      });
  }

  generateDSARForMPSAdmin(facilityId, patientId) {
    return http.request(
      API_ENDPOINTS.MPSAdminPatientDSAR(facilityId, patientId),
      "post"
    );
  }

  generateDSARForMPSSupport(facilityId, patientId) {
    return http.request(
      API_ENDPOINTS.MPSSupportPatientDSAR(facilityId, patientId),
      "post"
    );
  }

  updateStaffSpecialNotes = (patientId, note) => {
    return http.request(
      API_ENDPOINTS.staffSpecialNoteForPatient(patientId),
      "post",
      {
        note,
      }
    );
  };

  async getPatientContacts(patientId) {
    const data = await http.request(API_ENDPOINTS.patientContacts(patientId));
    if (data) {
      return {
        contacts: data._embedded ? data._embedded.contactPersons || [] : [],
        pageInfo: data.page,
      };
    } else {
      return {
        contacts: [],
        pageInfo: { number: -1 },
      };
    }
  }

  addContact(patientId, data) {
    return http.request(API_ENDPOINTS.patientContacts(patientId), "post", data);
  }

  getPatientContact(patientId, contactId) {
    return http.request(
      API_ENDPOINTS.patientContacts(patientId) + "/" + contactId
    );
  }
  removeContact(patientId, contactId) {
    return http.request(
      API_ENDPOINTS.patientContacts(patientId) + "/" + contactId,
      "delete"
    );
  }

  editContact(patientId, contactId, data) {
    return http.request(
      API_ENDPOINTS.patientContacts(patientId) + "/" + contactId,
      "patch",
      data
    );
  }

  async importPatientUsingCSV(
    patientTypeId,
    careTeamId,
    csvFile,
    invitePatientsOnLoad
  ) {
    const formData = new FormData();
    formData.append("files", csvFile);
    let api = API_ENDPOINTS.patientsImportCSV(patientTypeId);
    if (careTeamId) {
      api += "&careTeamId=" + careTeamId;
    }
    if (invitePatientsOnLoad) {
      api += "&invitePatientsOnLoad=true";
    }
    try {
      const response = await http.request(
        api,
        "post",
        formData,
        null,
        (progressEvent) => {
          const uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          store.dispatch(showProgress(uploadProgress));
        },
        120000
      );
      store.dispatch(closeProgress());
      let strings = [];
      if (response._embedded && response._embedded.strings) {
        strings = response._embedded.strings || [];
      }
      return strings;
    } catch (e) {
      store.dispatch(closeProgress());
    }
  }

  storePatientQuery(query) {
    let uiFieldsArr = preProcessFilterFields(query);
    let formData = createFormData(query, uiFieldsArr);
    formData.name = query.name;
    formData.visibility = query.visibility;
    formData.allowedCareTeams = query.allowedCareTeams;
    return http.request(API_ENDPOINTS.savedPatientQueries, "post", formData);
  }

  updatePatientQuery(queryId, query) {
    let uiFieldsArr = preProcessFilterFields(query);
    let formData = createFormData(query, uiFieldsArr);
    formData.name = query.name;
    formData.visibility = query.visibility;
    formData.allowedCareTeams = query.allowedCareTeams;
    return http.request(
      API_ENDPOINTS.savedPatientQueries + "/" + queryId,
      "patch",
      formData
    );
  }

  reorderPatientQueries(ids) {
    return http.request(API_ENDPOINTS.savedPatientQueriesOrder, "put", {
      ids,
    });
  }

  async loadAllSavedSearches() {
    const data = await http.request(
      API_ENDPOINTS.savedPatientQueries + "?page=0&size=100"
    );
    return data && data._embedded
      ? data._embedded.getPatientsFilterResources || []
      : [];
  }

  deletePatientQuery(queryId) {
    return http.request(
      API_ENDPOINTS.savedPatientQueries + "/" + queryId,
      "delete"
    );
  }

  fetchInteractionsReport(patientId) {
    return http.request(API_ENDPOINTS.interactions_report(patientId));
  }

  addPatient(data) {
    return http.request(API_ENDPOINTS.addPatientV2, "post", data);
  }

  importFromEHR(data) {
    return http
      .request(API_ENDPOINTS.addPatientV2, "post", { ...data, byPassInvite: true })
      .then((result) => {
        this.invalidatePatientCaches();
        return result;
      });
  }

  downloadAsCSV(filter) {
    let {
      patientTypeId,
      procedureDateStart,
      procedureDateEnd,
      creationDateStart,
      creationDateEnd,
      lastActiveDateStart,
      lastActiveDateEnd,
      referencedSurveyId,
      allStatus,
      selectedAttributes,
    } = filter;

    let api = API_ENDPOINTS.patientAsCSV(patientTypeId);
    if (procedureDateStart) {
      api +=
        "&procedureDateStart=" + moment(procedureDateStart).format("DDMMYYYY");
    }
    if (procedureDateEnd) {
      api += "&procedureDateEnd=" + moment(procedureDateEnd).format("DDMMYYYY");
    }
    if (creationDateStart) {
      api +=
        "&creationDateStart=" + moment(creationDateStart).format("DDMMYYYY");
    }
    if (creationDateEnd) {
      api += "&creationDateEnd=" + moment(creationDateEnd).format("DDMMYYYY");
    }

    if (lastActiveDateStart) {
      api +=
        "&lastActiveDateStart=" +
        moment(lastActiveDateStart).format("DDMMYYYY");
    }
    if (lastActiveDateEnd) {
      api +=
        "&lastActiveDateEnd=" + moment(lastActiveDateEnd).format("DDMMYYYY");
    }
    if (allStatus) {
      api += "&allStatus=true";
    }
    if (referencedSurveyId) {
      api += "&referencedSurveyId=" + referencedSurveyId;
    }

    let body = {};
    if (selectedAttributes && selectedAttributes.length >= 1) {
      body.attributeNames = selectedAttributes.map((a) => a.name);
    }

    return http.request(api, "post", body);
  }

  /**
   * Export patients to CSV using backend processing (staff listing)
   * @param {Object} criteria - Search/filter criteria (mode, searchTerm, uiFields, etc.)
   * @param {Array} selectedFields - Field names to include in export
   * @returns {Promise<string>} CSV text data
   */
  exportPatientsCSV(criteria, selectedFields) {
    const api = API_ENDPOINTS.staffPatientExport();
    const body = {
      mode: criteria?.mode || null,
      searchTerm: criteria?.searchTerm || null,
      patientTypeId: criteria?.patientTypeId || null,
      sortDirection: criteria?.sortDirection || null,
      selectedFields: selectedFields,
    };

    if (criteria?.uiFields && criteria.uiFields.length > 0) {
      body.uiFields = criteria.uiFields;
    }
    if (criteria?.sortField) {
      body.sortField = criteria.sortField;
    }
    if (criteria?.queryId) {
      body.queryId = criteria.queryId;
    }

    return http.request(api, "post", body);
  }

  updateChannel(channelId, channelData) {
    return http.request(API_ENDPOINTS.channel(channelId), "patch", channelData);
  }

  async loadDialysisCenters(patientId) {
    let api = API_ENDPOINTS.dialysisCenters(patientId);
    const data = await http.request(api);
    return {
      dialysisCenters:
        data && data.dialysisCenters && data.dialysisCenters.content
          ? data.dialysisCenters.content
          : [],
      patientPosition: data.patientPosition,
    };
  }

  downloadPatientData = (patientId) => {
    return http.request(
      API_ENDPOINTS.patientData(patientId),
      "post",
      null,
      null,
      null,
      null,
      {
        responseType: "arraybuffer",
      }
    );
  };

  resetCredentialsBySupport(patientId, data) {
    return http.request(
      API_ENDPOINTS.resetPatientCredentialsBySupport(patientId),
      "post",
      data
    );
  }

  async loadChargeItems(patientId, pageNumber, pageSize = 20) {
    let api =
      API_ENDPOINTS.chargeItems(patientId) +
      `?page=${pageNumber}&size=${pageSize}`;

    const data = await http.request(api);
    return {
      chargeItems:
        data && data._embedded && data._embedded.chargeItems
          ? data._embedded.chargeItems
          : [],
      pageInfo: data.page ? data.page : { number: 0, totalPages: 0 },
    };
  }
  uploadPatientProfilePhoto(patientId, files) {
    let endPoint = API_ENDPOINTS.uploadPatientProfilePhoto(patientId);
    return http.request(endPoint, "post", files);
  }

  setPatientProfilePhoto(files) {
    let endPoint = PATIENT_API_ENDPOINTS.setPatientProfilePhoto;
    return http.request(endPoint, "post", files);
  }

  loadPatientComorbidities(patientId) {
    return http.request(API_ENDPOINTS.getPatientComorbidities(patientId));
  }

  addPatientComorbidity(patientId, data) {
    return http.request(
      API_ENDPOINTS.addPatientComorbidity(patientId),
      "post",
      data
    );
  }

  deletePatientComorbidity(patientId, comorbidityId) {
    return http.request(
      API_ENDPOINTS.deletePatientComorbidity(patientId, comorbidityId),
      "delete"
    );
  }

  sendChatMessage(prompt) {
    let api = API_ENDPOINTS.chat();
    return http.request(api, "post", { prompt });
  }

  loadEpisodesOfCare(patientId) {
    return http.request(API_ENDPOINTS.getEpisodesOfCare(patientId), "get");
  }

  addEpisodeOfCare(patientId, data) {
    return http.request(
      API_ENDPOINTS.addEpisodeOfCare(patientId),
      "post",
      data
    );
  }

  deleteEpisodeOfCare(patientId, episodesId) {
    return http.request(
      API_ENDPOINTS.deleteEpisodeOfCare(patientId, episodesId),
      "delete"
    );
  }

  editEpisodeOfCare(patientId, episodesId, data) {
    return http.request(
      API_ENDPOINTS.editEpisodeOfCare(patientId, episodesId),
      "post",
      data
    );
  }

  loadEpisodesOfCare(patientId) {
    return http.request(API_ENDPOINTS.getEpisodesOfCare(patientId), "get");
  }

  activitiesForTodayCount() {
    return http.request(PATIENT_API_ENDPOINTS.activitiesForTodayCount, "get");
  }

  unreadMessagesCount() {
    return http.request(PATIENT_API_ENDPOINTS.unreadMessagesCount, "get");
  }

  appointmentsCount() {
    return http.request(PATIENT_API_ENDPOINTS.appointmentsCount, "get");
  }

  uploadDocument(document) {
    return http.request(PATIENT_API_ENDPOINTS.uploadDocument, "post", document);
  }
}

const patientsService = new PatientsService();

export default patientsService;
